import { useRef, useState, type ChangeEvent } from 'react'
import { UploadCloud } from 'lucide-react'
import { useSnackbar } from '@shared/ui/notification-context'
import { uploadDashboardAvatar, type DashboardUserProfile } from '@services/firebase'
import { getProfileFullName } from '@features/profile/shared/profile-helpers'
import { Card, CardContent, CardHeader, CardTitle, LmsButton } from '@shared/ui'
import '../profile-layout.css'

export function AvatarUploadCard({
  onUploaded,
  profile,
  shell = 'card',
}: {
  readonly onUploaded: (imageUrl: string) => void
  readonly profile: DashboardUserProfile
  readonly shell?: 'card' | 'plain'
}) {
  const { enqueueSnackbar } = useSnackbar()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [progressValue, setProgressValue] = useState<number | null>(null)

  const handleFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (!file) {
      return
    }

    setErrorMessage(null)
    setIsUploading(true)
    setProgressValue(20)

    try {
      const imageUrl = await uploadDashboardAvatar({
        file,
        previousImageUrl: profile.imageUrl,
      })

      setProgressValue(100)
      onUploaded(imageUrl)
      enqueueSnackbar('Updated successfully', { variant: 'success' })
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Avatar upload failed.',
      )
      enqueueSnackbar('Updated failed', { variant: 'error' })
    } finally {
      setIsUploading(false)

      if (inputRef.current) {
        inputRef.current.value = ''
      }
    }
  }

  const content = (
    <>
      <div className="profileSectionStack profileTextCenter">
        <span className="profileAvatar profileAvatarUpload">
          {profile.imageUrl ? (
            <img alt="" className="profileAvatarImage" src={profile.imageUrl} />
          ) : (
            getProfileFullName(profile).charAt(0)
          )}
        </span>
        <p className="profileMutedText">
          Choose a learner avatar image and the existing dashboard profile will
          be updated in place.
        </p>
        <input
          accept="image/*"
          hidden
          onChange={(event) => void handleFileSelected(event)}
          ref={inputRef}
          type="file"
        />
        <LmsButton
          isLoading={isUploading}
          leadingIcon={
            isUploading ? undefined : (
              <UploadCloud aria-hidden="true" size={18} strokeWidth={2.3} />
            )
          }
          loadingLabel="Uploading"
          onClick={() => {
            inputRef.current?.click()
          }}
          variant="primary"
        >
          Upload avatar
        </LmsButton>
      </div>

      {progressValue !== null ? (
        <div
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={progressValue}
          className="profileProgressTrack"
          role="progressbar"
        >
          <div className="profileProgressValue" style={{ width: `${progressValue}%` }} />
        </div>
      ) : null}

      {errorMessage ? (
        <p className="profileErrorState" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </>
  )

  if (shell === 'plain') {
    return (
      <div className="profileCardStack">
        <h2 className="profileCardTitle">Upload avatar</h2>
        {content}
      </div>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upload avatar</CardTitle>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  )
}
